import React, { useState, useEffect } from 'react';
import { getFromHub, postToHub } from '../network/actions';
import { Password } from 'primereact/password';
import { checkRule } from '../utils/validationUtils';

function ChangePasswordStep({ isFirstLogin, onPasswordChanged, onBack }) {
    const [policy, setPolicy] = useState([]);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPass] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isFetchingPolicy, setIsFetchingPolicy] = useState(true);
    const [fetchError, setFetchError] = useState('');

    const loadPolicy = () => {
        setIsFetchingPolicy(true);
        setFetchError('');
        getFromHub(
            'authentication/fetchPasswordPolicy',
            (data) => { // success
                if (Array.isArray(data)) {
                    setPolicy(data);
                };
                setIsFetchingPolicy(false);
            },
            (error) => { // failure
                console.log(error);
                setFetchError('Failed to load password policy. Please try again.');
                setIsFetchingPolicy(false);
            }
        );
    };

    useEffect(() => {
        loadPolicy();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match.');
            return;
        }

        for (const rule of policy) {
            if (rule.regex) {
                try {
                    const regex = new RegExp(rule.regex);
                    if (!regex.test(newPassword)) {
                        setError(rule.message || 'Password does not meet the requirements.');
                        return;
                    }
                } catch (err) {
                    console.error("Invalid regex in password policy:", rule.regex);
                }
            }
        }

        setLoading(true);
        setError('');
        try {
            await postToHub(
                'authentication/updatePassword',
                { newPassword, oldPassword },
                () => { // success
                    onPasswordChanged();
                },
                (error) => { // failure
                    if (error.response.data) {
                        setError(error.response.data);
                    }
                    else {
                        setError('Failed to update password. Please try again.');
                    }
                }
            );
        } catch {
            setError('Failed to update password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (isFetchingPolicy) {
        return (
            <div className="flex flex-col items-center py-8">
                <p className="text-gray-500 text-sm">Loading Password Policy…</p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex flex-col items-center pb-8 text-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-700 text-sm">{fetchError}</p>
                </div>
                <button
                    type="button"
                    onClick={loadPolicy}
                    className="text-sm text-blue-600 hover:text-blue-800 underline mb-4"
                >
                    Try again
                </button>
                {!isFirstLogin && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="text-sm text-gray-500 hover:text-gray-700"
                    >
                        ← Back to sign in
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                    Change your password
                </h2>
                <p className="text-xs text-gray-500">
                    {isFirstLogin
                        ? 'Set a new password to continue'
                        : 'Your password has expired. Please set a new one.'}
                </p>
            </div>

            {policy.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Password requirements:</p>
                    <ul className="space-y-1.5 flex flex-col justify-center">
                        {policy.map((rule, i) => {
                            const isFulfilled = checkRule(rule.regex, newPassword);
                            return (
                                <li key={i} className={`text-xs flex items-center ${isFulfilled ? 'text-green-600' : 'text-gray-500'}`}>
                                    <span className="mr-2 sm:text-sm">{isFulfilled ? '✓' : '○'}</span>
                                    <span>{rule.message || rule}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <div>
                    <label htmlFor="old-password" className="block text-xs font-medium text-gray-600 mb-1">
                        Current password
                    </label>
                    <Password
                        id="old-password"
                        value={oldPassword}
                        onChange={(e) => { setError(''); setOldPassword(e.target.value); }}
                        className="w-full"
                        inputClassName="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700"
                        feedback={false}
                        required
                    />
                </div>

                <div>
                    <label htmlFor="new-password" className="block text-xs font-medium text-gray-600 mb-1">
                        New password
                    </label>
                    <Password
                        id="new-password"
                        value={newPassword}
                        onChange={(e) => { setError(''); setNewPass(e.target.value); }}
                        className="w-full"
                        inputClassName="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700"
                        feedback={false}
                        required
                    />
                </div>

                <div>
                    <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-600 mb-1">
                        Confirm new password
                    </label>
                    <Password
                        id="confirm-password"
                        value={confirmPassword}
                        onChange={(e) => { setError(''); setConfirmPassword(e.target.value); }}
                        className="w-full"
                        inputClassName="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700"
                        feedback={false}
                        required
                    />
                </div>

                {error && (
                    <div className="text-red-700">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={
                        loading ||
                        !newPassword ||
                        !confirmPassword ||
                        newPassword !== confirmPassword
                    }
                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors"
                >
                    {loading ? 'Updating…' : 'Update password'}
                </button>

                {!isFirstLogin && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="text-sm text-gray-500 hover:text-gray-700 text-center"
                    >
                        ← Back to sign in
                    </button>
                )}
            </form>
        </div>
    );
}

export default ChangePasswordStep;
